import { formatiereGehzeit } from './einheiten'
/**
 * Geo Utilities — Single Source of Truth
 *
 * All geographic calculations in one place.
 * Replaces duplicate implementations across:
 * - tile-map/ArtrackService (private haversineDistance)
 * - guide/ZoneMatcher (haversineDistance)
 * - NavigatePage (inline haversineDistance, isInSchlucht)
 * - NavigationMap (inline haversine)
 */

// ── Distance ────────────────────────────────────────────────────

/** Calculate distance between two GPS coordinates in meters (Haversine formula) */
export function haversine(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const R = 6371000 // Earth radius in meters
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// ── Formatting ──────────────────────────────────────────────────

/** Format meters as human-readable distance ("42 m" or "1.3 km") */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${(meters / 1000).toFixed(1)} km`
}

/** Gehzeit aus einer Distanz — in den Kurzformen der aktiven Sprache
 *  („18 min" / „1 hr 18 min"). Die Kuerzel standen frueher hier fest und
 *  erschienen deshalb auch auf Englisch deutsch. */
export function formatWalkingTime(meters: number, speedKmh = 3.5): string {
  return formatiereGehzeit(((meters / 1000) / speedKmh) * 60)
}

/** Format seconds as mm:ss */
export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// ── Bounding Box (config-driven) ────────────────────────────────

import { getConfig } from '../config/project'

/** Project bounding box (from config, fallback to Tscheppaschlucht) */
export const SCHLUCHT_BOUNDS = getConfig().bounds ?? {
  minLat: 46.478,
  maxLat: 46.500,
  minLng: 14.255,
  maxLng: 14.290,
}

/** Check if coordinates are within the project area */
export function isInSchlucht(lat: number, lng: number): boolean {
  return (
    lat >= SCHLUCHT_BOUNDS.minLat && lat <= SCHLUCHT_BOUNDS.maxLat &&
    lng >= SCHLUCHT_BOUNDS.minLng && lng <= SCHLUCHT_BOUNDS.maxLng
  )
}

// ── Route Distance ──────────────────────────────────────────────

/** 
 * Calculate route-based distance between two "along meters" positions.
 * Returns the absolute distance along the route path.
 */
export function routeDistance(alongMeters1: number, alongMeters2: number): number {
  return Math.abs(alongMeters1 - alongMeters2)
}

/**
 * Calculate total route length from GPS points.
 */
export function totalRouteLength(points: Array<{ latitude: number; longitude: number }>): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total += haversine(
      points[i - 1].latitude, points[i - 1].longitude,
      points[i].latitude, points[i].longitude,
    )
  }
  return total
}

/**
 * Find nearest point index on a route polyline.
 * Returns { index, distance, alongMeters }.
 */
export function findNearestPointOnRoute(
  lat: number, lng: number,
  points: Array<{ latitude: number; longitude: number }>,
): { index: number; distance: number; alongMeters: number } {
  let minDist = Infinity
  let nearestIdx = 0

  for (let i = 0; i < points.length; i++) {
    const d = haversine(lat, lng, points[i].latitude, points[i].longitude)
    if (d < minDist) {
      minDist = d
      nearestIdx = i
    }
  }

  // Calculate along-route distance to nearest point
  let alongMeters = 0
  for (let i = 1; i <= nearestIdx; i++) {
    alongMeters += haversine(
      points[i - 1].latitude, points[i - 1].longitude,
      points[i].latitude, points[i].longitude,
    )
  }

  return { index: nearestIdx, distance: minDist, alongMeters }
}
